import { type BaseFilterOption, FILTER_TYPE_BASE, type FilterFieldType } from '@/services/tables/filters/fields/types'
import type { Operation } from '@/services/types'
import { isSameFilterValue } from '@/services/tables/filters/utils/valueComparison'
import type { IconName } from '@/assets/icons/icons'
import type { SelectOption } from '@/services/forms/types'
import type { ParsedFilterType } from '@/services/tables/types'

/**
 * Filter field state. Every entry is an own property of the instance, because consumers
 * spread filter fields (`{ ...field, options }`) and prototype getters would not survive that
 */
export type FilterFieldData = {
  id: string
  formId: string
  filterType: FilterFieldType
  name: string
  queryName: string
  label: string
  relatedFieldName: string
  isHidden: boolean
  isVisible: boolean
  order: number
  value: unknown
  selectedItemsValue: SelectOption[]
  innerValue: unknown
  innerSelectedItemsValue: SelectOption[]
  initialValue: unknown
  availableOperations: Operation[]
  operation: Operation
  innerOperation: Operation
  initialOperation: Operation
  isApplied: boolean
  options: BaseFilterOption[]
  readonly: boolean
  isDefault: boolean
  openOnAdd: boolean
  icon: IconName
}

export class FilterField {
  public id = ''

  public formId = ''

  /** Picks the strategy that reads and writes this field, see FilterStrategyManager */
  public filterType: FilterFieldType = FILTER_TYPE_BASE

  public name = ''

  public queryName = ''

  public label = ''

  public relatedFieldName = 'id'

  public isHidden = false

  public isVisible = false

  public order = 0

  public value: unknown = ''

  public selectedItemsValue: SelectOption[] = []

  public innerValue: unknown = ''

  public innerSelectedItemsValue: SelectOption[] = []

  public initialValue: unknown = ''

  public availableOperations: Operation[] = []

  public operation: Operation = 'in'

  public innerOperation: Operation = 'in'

  public initialOperation: Operation = 'in'

  public isApplied = true

  public options: BaseFilterOption[] = []

  public readonly = false

  public isDefault = false

  public openOnAdd = false

  public icon: IconName = ''

  public patch(changes: Partial<FilterFieldData>) {
    Object.assign(this, changes)
  }

  public getApiValue(): unknown {
    return this.value ?? ''
  }

  public reset() {
    this.value = ''
  }

  public getName(): string {
    return this.name
  }

  public getQueryName(): string {
    return this.queryName || this.name
  }

  public getFieldType(): string {
    return 'text'
  }

  public getCurrentOperation(): Operation {
    return this.operation
  }

  /** Several filters can share one query name, so the relation field decides which of them an entry belongs to */
  public matchesPayloadEntry(item: ParsedFilterType): boolean {
    if (item.name !== this.getQueryName()) {
      return false
    }

    if (!this.relatedFieldName) {
      return true
    }

    // Declaring a query name means sharing the field, and an entry without a relation fits none of the sharers
    if (this.queryName) {
      return item.relatedField === this.relatedFieldName
    }

    return !item.relatedField || item.relatedField === this.relatedFieldName
  }

  public getRelationField(): string {
    return this.relatedFieldName
  }

  public isChangedAndApplied(): boolean {
    return this.isApplied && (!!this.initialValue || this.isChanged())
  }

  public isChanged(): boolean {
    return !isSameFilterValue(this.value, this.initialValue)
  }
}
